<template>
    <article class="card bg-light mb-3"
        :class="{active: isActive}"
        itemscope itemtype="http://schema.org/Book" :id="'book-card-' + book.id">
        <div class="filtered" v-if="isFilter" @click="showR18()">
            <span class="badge badge-danger">R18作品</span>
        </div>
        <div class="row no-gutters">
            <div class="col-xl img-column" v-if="book.cover_url">
                <component
                    :is="isInternal(bookLink) ? 'router-link' : 'a'"
                    :to="isInternal(bookLink) ? bookLink : ''"
                    :href="!isInternal(bookLink) ? bookLink : ''"
                    :target="isInternal(bookLink) ? '_self' : subdomain">
                    <img v-lazy="book.cover_url.replace(/\.webp$/, '_thumb.webp') + '?' + book.cover_hash" class="mx-auto d-block cover" />
                </component>
            </div>
            <div class="col-xl img-column position-relative" v-else>
                <component
                    :is="isInternal(bookLink) ? 'router-link' : 'a'"
                    :to="isInternal(bookLink) ? bookLink : ''"
                    :href="!isInternal(bookLink) ? bookLink : ''"
                    :target="isInternal(bookLink) ? '_self' : subdomain">
                    <img v-lazy="cover()" class="mx-auto d-block cover" />
                    <div class="book-text">
                        <div class="cover-text vertical">
                            <h3 class="novel-title mincho" v-html="$filters.ruby(book.title)"></h3>
                            <p v-if="book.subtitle" v-html="$filters.ruby(book.subtitle)"></p>
                        </div>
                    </div>
                </component>
            </div>
            <div class="col-xl">
                <div class="card-body">
                    <div class="row">
                        <div class="col-lg-9">
                            <component
                                :is="isInternal(bookLink) ? 'router-link' : 'a'"
                                :to="isInternal(bookLink) ? bookLink : ''"
                                :href="!isInternal(bookLink) ? bookLink : ''"
                                :target="isInternal(bookLink) ? '_self' : subdomain"
                                class="black">
                                <div class="card-title">
                                    <div class="small mincho" v-if="book.series">
                                        {{book.series.series_name}}
                                        <i class="fas fa-layer-group" v-if="book.series.is_group"></i>
                                    </div>
                                    <h3 class="title mincho" itemprop="title" v-html="$filters.ruby(book.title)"></h3>
                                    <span class="badge" :class="['badge-' + book.rating.color]" v-if="book.rating">{{book.rating.rating_name}}</span>
                                    <span class="badge badge-secondary" v-if="!book.is_long">短編</span>
                                    <span v-if="author" itemprop="author">／{{book.author.name}}</span>
                                </div>
                            </component>
                            <component
                                :is="isInternal(bookLink) ? 'router-link' : 'a'"
                                :to="isInternal(bookLink) ? bookLink : ''"
                                :href="!isInternal(bookLink) ? bookLink : ''"
                                :target="isInternal(bookLink) ? '_self' : subdomain"
                                class="black">
                                <h4 class="card-subtitle mincho" itemprop="subtitle" v-if="book.subtitle" v-html="$filters.ruby(book.subtitle)"></h4>
                            </component>
                        </div>
                        <div class="col-lg-3 mt-2">
                            <button type="button"
                                class="btn btn-sm btn-block"
                                :class="{'btn-outline-success': !book.fan, 'btn-outline-secondary': book.fan}"
                                :disabled="!canFollow"
                                @click="toggleFavorite()"
                                v-if="favorite">
                                <span v-if="book.fan">
                                    <i class="fas fa-heart-broken"></i>
                                    お気に入り削除
                                </span>
                                <span v-else>
                                    <i class="fas fa-heart"></i>
                                    お気に入り追加
                                </span>
                            </button>
                        </div>
                    </div>
                    <div class="card-text b-air desc" itemprop="description">{{book.description}}</div>
                    <div v-if="book.tags">
                        <ul class="tag-list">
                            <li v-for="t in book.tags" :key="t.id">
                                <router-link :to="'/search?keywords=' + encodeURI(t.tag_name)" class="black">{{t.tag_name}}</router-link>
                            </li>
                        </ul>
                    </div>
                    <div class="properties small" v-if="!footer">
                        <ul class="horizontal-list">
                            <li>{{$filters.numberFormat(book.property.published_chars )}} 文字</li>
                            <li><time :datetime="book.property.last_published_at" itemprop="dateModified">{{$filters.dateFormat(book.property.last_published_at , 'YYYY年MM月DD日')}}</time> 更新</li>
                            <li><time :datetime="book.property.first_published_at" itemprop="datePublished">{{$filters.dateFormat(book.property.first_published_at , 'YYYY年MM月DD日')}}</time> 公開</li>
                        </ul>
                    </div>
                </div>
            </div>
        </div>
        <div class="card-footer small" v-if="footer">
            <div class="row">
                <div class="col-4">
                    <span v-if="book.property.is_finished" class="badge badge-dark badge-pill">完結</span>
                    <span v-else-if="book.is_finish" class="badge badge-success badge-pill">完結予定</span>
                    <span v-else class="badge badge-primary badge-pill">連載中</span>
                </div>
                <div class="col-8 text-right">
                    <ul class="horizontal-list">
                        <li>{{$filters.numberFormat(book.property.published_chars )}} 文字</li>
                        <li><time :datetime="book.property.last_published_at" itemprop="dateModified">{{$filters.dateFormat(book.property.last_published_at , 'YYYY年MM月DD日')}}</time> 最終更新</li>
                        <li><time :datetime="book.property.first_published_at" itemprop="datePublished">{{$filters.dateFormat(book.property.first_published_at , 'YYYY年MM月DD日')}}</time> 初回公開</li>
                    </ul>
                </div>
            </div>
        </div>
    </article>
</template>

<style scoped lang="scss">
.active {
    border: 1px solid maroon;
}
.card-subtitle {
    font-size: 1rem;
}
.cover {
    object-fit: cover;
    height: 352px;
    width: 250px;
}
.card {
    overflow: hidden;
}
.desc {
    display: -webkit-box;
    margin: .7rem 0;
    padding: .3rem .7rem;
    border-radius: .5rem;
    font-size: .8rem;
    -webkit-line-clamp: 4;
    -webkit-box-orient: vertical;
    overflow: hidden;
}
.properties {
    position: absolute;
    bottom: .5rem;
    right: .5rem;
}
.img-column {
    max-width: 250px;
    .book-text {
        position: absolute;
        top: 0;
        left: 0;
        padding: 2.3rem;
        height: 352px;
        min-width: 250px;
        width: 100%;
        overflow: hidden;
        text-align: center;
        color: snow;
        .cover-text {
            display: inline-block;
            text-align: left;
            h3 {
                font-size: 1.0rem;
            }
            p {
                font-size: .7rem;
            }
        }
    }
}
.black {
    color: #35393b;
    &:hover {
        color: #2f424b;
        text-shadow: 0 0 9px rgba(0, 0, 127, .2);
        text-decoration: none;
    }
}
.title {
    display: inline-block;
}
.badge {
    font-size: .8rem;
    margin: 0 .3rem;
}
.icon {
    width: 2.0rem;
    height: 2.0rem;
    display: inline-block;
    margin: 0 .3rem 0 0;
}
@media screen and (max-width: 1200px) {
    .img-column {
        max-width: none;
    }
    .properties {
        position: relative;
        bottom: unset;
        right: unset;
    }
}
@media screen and (max-width: 770px) {
    .cover {
        object-fit: cover;
        height: 100%;
        width: 100%;
    }
    .title {
        font-size: 1.0rem;
    }
    .img-column {
        .book-text {
            height: 100%;
            min-width: 250px;
            width: 100%;
            .cover-text {
                h3 {
                    font-size: 1.0rem;
                }
                p {
                    font-size: .7rem;
                }
            }
        }
    }
}
</style>

<script>
export default {
    props: {
        book: {
            required: true
        },
        // お気に入りボタン表示
        favorite: {
            default: true
        },
        // 作者名を表示
        author: {
            default: false
        },
        isActive: {
            default: false,
        },
        // フッター表示
        footer: {
            default: true,
        },
        // ドメイン遷移をしない
        directLink: {
            default: false,
        }
    },
    methods: {
        async toggleFavorite() {
            try {
                const favorite = await axios.post("/author/book/favorite/toggle", {'id': this.book.id});
                alert.msg(favorite.data);
                this.$emit("updated");
            } catch (err) {
                alert.err(err.response);
            }
        },
        isInternal(link) {
            return !link.match(/^(https?:)?\/\//);
        },
        showR18() {
            $("#r18-modal").modal();
        },
        cover() {
            const index = Math.floor(Math.random() * this.$store.getters.novelCovers.length);
            return this.$store.getters.novelCovers[index];
        }
    },
    computed: {
        bookLink() {
            let url = "";
            if (!this.directLink) {
                if (this.subdomain + this.$store.state.base_domain !== location.hostname) {
                    // ドメイン判定
                    url = `//${this.subdomain}${this.$store.state.base_domain}`;
                }
            }

            if (this.book.is_long) {
                // 長編
                url += `/author/book/${this.book.id}`;
            } else {
                // 短編
                url += `/author/page/${this.book.id}/section/${this.book.sections[0].id}`;
            }
            return url;
        },
        canFollow() {
            if (this.$store?.state?.user) {
                if (this.$store.state.user.id == this.book.author?.id) {
                    // 自分をフォローできない
                    return false;
                }
                return true;
            }
            return false;
        },
        subdomain() {
            let subdomain = "www";
            if (this.book.author && this.book.author.subdomain) {
                subdomain = this.book.author.subdomain.toLowerCase();
            }
            return subdomain;
        },
        isFilter() {
            if (this.book?.rating?.rating_level >= 2 && !this.$store.state.user && !this.$store.state.r18) {
                return true;
            }
            return false;
        },
    },
}
</script>